import { useMemo, useCallback, useState, useEffect } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
} from "@headlessui/react";
import { XMarkIcon, ComputerDesktopIcon } from "@heroicons/react/24/outline";
import { LibraryBrowser } from "@/ui-components/LibraryBrowser";
import { portfolioToLibrarySections } from "@/ui-components/LibraryBrowser/adapters";
import { menuCategories } from "@/widget-components/newsquawk-headlines/models/categories";
import { getTagsByIds } from "@/shared/api/newsquawk-headlines";
import { useFilterBuilder } from "../hooks/useFilterBuilder";
import QueryBuilderPane from "./QueryBuilderPane";

// Static seed from menuCategories
const menuTagIdToName: Record<string, string> = {};
menuCategories.forEach((cat) => {
  cat.items.forEach((item) => {
    menuTagIdToName[String(item.id)] = item.title;
  });
});

interface FilterBuilderBrowserProps {
  isOpen: boolean;
  onClose: () => void;
  queryId?: number | null;
  /** Concierge draft pre-fill (create mode) */
  draft?: {
    name?: string;
    searchKeyword?: string;
    tags?: string[];
    filterMode?: "AND" | "OR";
    keywordOp?: "AND" | "OR";
  } | null;
}

export default function FilterBuilderBrowser({
  isOpen,
  onClose,
  queryId,
  draft,
}: FilterBuilderBrowserProps) {
  const builder = useFilterBuilder({
    queryId,
    draft,
    onSaved: () => onClose(),
    onDeleted: () => onClose(),
  });

  const handleClose = useCallback(() => {
    builder.reset();
    onClose();
  }, [builder.reset, onClose]);

  // Dynamic tag name resolution
  const [resolvedTagNames, setResolvedTagNames] = useState<Record<string, string>>({});

  const tagIdToName = useMemo(
    () => ({ ...menuTagIdToName, ...resolvedTagNames }),
    [resolvedTagNames]
  );

  useEffect(() => {
    const allTagIds = builder.tagsWatchlists.flatMap((tw) => tw.tags);
    const unknownIds = allTagIds.filter((id) => !menuTagIdToName[id] && !resolvedTagNames[id]);
    if (unknownIds.length === 0) return;

    const uniqueIds = [...new Set(unknownIds)];
    getTagsByIds(uniqueIds.join(" "))
      .then((tagDetails) => {
        const newNames: Record<string, string> = {};
        if (tagDetails) {
          for (const [tagId, detail] of Object.entries(tagDetails)) {
            const primary = detail.labels?.find((l) => l.primary);
            const name = primary?.name || detail.labels?.[0]?.name;
            if (name) newNames[tagId] = name;
          }
        }
        if (Object.keys(newNames).length > 0) {
          setResolvedTagNames((prev) => ({ ...prev, ...newNames }));
        }
      })
      .catch(() => {});
  }, [builder.tagsWatchlists]);

  // Build LibrarySection[] from portfolios + tags
  const sections = useMemo(() => {
    return portfolioToLibrarySections(
      builder.portfolios,
      builder.tagsWatchlists,
      [],
      { tagIdToName }
    );
  }, [builder.portfolios, builder.tagsWatchlists, tagIdToName]);

  // Map builder selections to LibraryBrowser selections format
  // The builder tracks portfolioIds, categoryIds, and tagWatchlistIds separately.
  // We need to map them to section-based selections for the LibraryBrowser.
  const browserSelections = useMemo<Record<string, string[]>>(() => {
    const sel: Record<string, string[]> = {};

    // Portfolio IDs map to my/nq ticker/keyword sections
    if (builder.selectedPortfolioIds.length > 0) {
      const myTickerIds: string[] = [];
      const nqTickerIds: string[] = [];
      const myKeywordIds: string[] = [];
      const nqKeywordIds: string[] = [];

      for (const id of builder.selectedPortfolioIds) {
        const p = builder.portfolios.find((port) => port.id === id);
        if (!p) continue;
        const isKeywordOnly = (p.keywords?.length ?? 0) > 0 && p.tickers.length === 0;
        const isNewsquawk = p.owner_type === "NEWSQUAWK";

        if (isKeywordOnly) {
          (isNewsquawk ? nqKeywordIds : myKeywordIds).push(String(id));
        } else {
          (isNewsquawk ? nqTickerIds : myTickerIds).push(String(id));
        }
      }

      if (myTickerIds.length > 0) sel["my-ticker-watchlists"] = myTickerIds;
      if (myKeywordIds.length > 0) sel["my-keyword-watchlists"] = myKeywordIds;
      // All Newsquawk items go under unified nq-library section
      const nqIds = [...nqTickerIds, ...nqKeywordIds];
      if (nqIds.length > 0) sel["nq-library"] = nqIds;
    }

    // Tag watchlist IDs
    if (builder.selectedTagsWatchlistIds.length > 0) {
      const userTagIds = builder.selectedTagsWatchlistIds
        .filter((id) => {
          const tw = builder.tagsWatchlists.find((t) => t.id === id);
          return tw?.owner_type === "USER";
        })
        .map((id) => `tag-${id}`);
      const nqTagIds = builder.selectedTagsWatchlistIds
        .filter((id) => {
          const tw = builder.tagsWatchlists.find((t) => t.id === id);
          return tw?.owner_type !== "USER";
        })
        .map((id) => `tag-${id}`);

      if (userTagIds.length > 0) sel["my-tag-watchlists"] = userTagIds;
      // Newsquawk tag IDs go under unified nq-library section
      if (nqTagIds.length > 0) {
        sel["nq-library"] = [...(sel["nq-library"] || []), ...nqTagIds];
      }
    }

    // Individual tag categories → go to newsquawk library section
    if (builder.selectedCategories.length > 0) {
      const existing = sel["nq-library"] || [];
      sel["nq-library"] = [
        ...existing,
        ...builder.selectedCategories.map((c) => String(c.id)),
      ];
    }

    return sel;
  }, [builder.selectedPortfolioIds, builder.selectedTagsWatchlistIds, builder.selectedCategories, builder.portfolios]);

  // Handle toggle from LibraryBrowser
  const handleToggle = useCallback(
    (_sectionId: string, itemId: string) => {
      // Tag watchlist items have "tag-" prefix
      if (itemId.startsWith("tag-")) {
        const twId = Number(itemId.replace("tag-", ""));
        if (!isNaN(twId)) {
          builder.toggleTagWatchlist(twId);
        }
        return;
      }

      // Try as a portfolio ID first (tickers/keywords)
      const numId = Number(itemId);
      if (!isNaN(numId)) {
        const portfolio = builder.portfolios.find((p) => p.id === numId);
        if (portfolio) {
          builder.togglePortfolio(numId);
          return;
        }

        // Otherwise it's an individual tag category
        const tagItem = menuCategories
          .flatMap((c) => c.items)
          .find((item) => item.id === numId);
        if (tagItem) {
          builder.toggleCategory({ title: tagItem.title, id: tagItem.id });
        }
      }
    },
    [builder.togglePortfolio, builder.toggleTagWatchlist, builder.toggleCategory, builder.portfolios]
  );

  // Filter out advanced filters since we're building one — strip from both
  // standalone sections and the Newsquawk Library's groups
  const filteredSections = useMemo(
    () => sections
      .filter((s) => s.id !== "my-filters")
      .map((s) => {
        if (s.id === "nq-library") {
          const groups = s.groups.filter((g) => g.id !== "newsquawk-filters");
          return groups.length > 0 ? { ...s, groups } : null;
        }
        return s;
      })
      .filter((s): s is NonNullable<typeof s> => s !== null),
    [sections]
  );

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  return (
    <Dialog open={isOpen} onClose={handleClose} className="no-drag relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-900/90 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200"
      />
      <div className="fixed inset-0 z-10 flex items-center justify-center p-2 md:p-4">
        <DialogPanel
          transition
          className="w-full h-[90vh] mx-2 md:mx-4 transform overflow-hidden rounded-lg bg-gray-800 shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[closed]:sm:scale-95"
        >
          {/* Mobile fallback */}
          {isMobile ? (
            <div className="h-full flex flex-col items-center justify-center px-8 text-center">
              <ComputerDesktopIcon className="size-12 text-gray-600 mb-4" />
              <h3 className="text-sm font-semibold text-gray-200 mb-2">Desktop Required</h3>
              <p className="text-xs text-gray-500 mb-6">
                Advanced filter editing is available on larger screens.
              </p>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium rounded bg-gray-700 text-gray-200 hover:bg-gray-600 transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
          <LibraryBrowser
            mode="builder"
            sections={filteredSections}
            selections={browserSelections}
            onSelectionsChange={() => {}}
            onToggleOverride={handleToggle}
            className="h-full !rounded-none !border-0"
            sidebarTitle="Filter Components"
            defaultActivePath={["nq-library"]}
            customRightPane={<QueryBuilderPane builder={builder} onCancel={handleClose} />}
            header={
              <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-700/50 bg-gray-800">
                <h2 className="text-sm font-semibold text-gray-100 flex-1">
                  {builder.isEditing ? "Edit Advanced Filter" : "Create Advanced Filter"}
                </h2>
                <button
                  onClick={handleClose}
                  className="p-1 text-gray-400 hover:text-gray-200 transition-colors"
                >
                  <XMarkIcon className="size-5" />
                </button>
              </div>
            }
          />
          )}
        </DialogPanel>
      </div>
    </Dialog>
  );
}
